import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for managing multi-select state
 * @param {Array} items - Array of items with unique 'path' property
 * @returns {object} Selection state and operations
 */
export const useSelection = (items = []) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const lastSelectedRef = useRef(null);

  // Get ordered list of item paths for range selection
  const itemPaths = items.map((item) => item.path);

  // Clear selection when items change significantly
  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set();
      prev.forEach((id) => {
        if (itemPaths.includes(id)) {
          validIds.add(id);
        }
      });
      return validIds.size !== prev.size ? validIds : prev;
    });
  }, [itemPaths.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if an item is selected
  const isSelected = useCallback(
    (id) => selectedIds.has(id),
    [selectedIds]
  );

  // Toggle single item selection
  const toggleSelect = useCallback((id, event) => {
    const isCtrlOrCmd = event?.metaKey || event?.ctrlKey;
    const isShift = event?.shiftKey;

    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (isShift && lastSelectedRef.current) {
        // Range selection
        const startIdx = itemPaths.indexOf(lastSelectedRef.current);
        const endIdx = itemPaths.indexOf(id);
        if (startIdx !== -1 && endIdx !== -1) {
          const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
          for (let i = from; i <= to; i++) {
            next.add(itemPaths[i]);
          }
        }
      } else if (isCtrlOrCmd) {
        // Toggle individual item
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
      } else {
        // Single selection (clear others)
        next.clear();
        next.add(id);
      }

      lastSelectedRef.current = id;
      return next;
    });
  }, [itemPaths]);

  // Select all items
  const selectAll = useCallback(() => {
    setSelectedIds(new Set(itemPaths));
  }, [itemPaths]);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedRef.current = null;
  }, []);

  // Select specific items by IDs
  const setSelection = useCallback((ids) => {
    setSelectedIds(new Set(ids));
  }, []);

  // Get selected items
  const getSelectedItems = useCallback(() => {
    return items.filter((item) => selectedIds.has(item.path));
  }, [items, selectedIds]);

  // Keyboard handler for Ctrl+A and Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle if not in an input/textarea
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable
      ) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'a') {
        event.preventDefault();
        selectAll();
      } else if (event.key === 'Escape') {
        clearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectAll, clearSelection]);

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    isSelected,
    toggleSelect,
    selectAll,
    clearSelection,
    setSelection,
    getSelectedItems,
    hasSelection: selectedIds.size > 0,
  };
};

export default useSelection;
